import { useState } from "react";
import {
  Zap,
  Bell,
  CheckSquare,
  FileText,
  Mail,
  Phone,
  BarChart3,
  Settings,
  Users,
  Building2,
  Briefcase,
  Monitor,
  UserPlus,
  ChevronDown,
  ChevronRight,
  PanelLeftClose
} from "lucide-react";
import { OnboardingWidget } from "./OnboardingWidget";

const navItems = [
  { icon: Zap, label: "Quick Actions", shortcut: "⌘K" },
  { icon: Bell, label: "Notifications" },
  { icon: CheckSquare, label: "Tasks" },
  { icon: FileText, label: "Notes" },
  { icon: Mail, label: "Emails" },
  { icon: Phone, label: "Calls" },
  { icon: BarChart3, label: "Reports" },
  { icon: Settings, label: "Automations", hasSubmenu: true },
];

const recordItems = [
  { icon: Users, label: "People", iconBg: "bg-[#00b9eb]", active: false },
  { icon: Building2, label: "Companies", iconBg: "bg-[#266df0]", active: true },
  { icon: Briefcase, label: "Deals", iconBg: "bg-[#f97514]", active: false },
  { icon: Monitor, label: "Workspaces", iconBg: "bg-[#9b69ff]", active: false },
  { icon: UserPlus, label: "Users", iconBg: "bg-[#00d17e]", active: false },
];

interface SidebarProps {
  onToggle?: () => void;
}

export function Sidebar({ onToggle }: SidebarProps) {
  const [automationsExpanded, setAutomationsExpanded] = useState(false);
  const [recordsExpanded, setRecordsExpanded] = useState(true);

  return (
    <div className="w-72 h-full bg-white border-r border-gray-200 flex flex-col">
      {/* Header */}
      <div className="bg-[#fbfbfb] border-b border-[#eeeff1] flex items-center justify-center gap-[12px] px-[12px] pr-[16px] py-0 h-[48px]">
        <div className="w-[24px] h-[24px] rounded-[7px] overflow-hidden border border-[rgba(0,0,0,0.05)]">
          <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900"></div>
        </div>
        <div className="flex-1 flex items-center gap-1">
          <span className="font-semibold text-[16px] text-[#242629] leading-5 tracking-[-0.16px] whitespace-nowrap">
            Basepoint
          </span>
          <ChevronDown className="w-[14px] h-[14px] text-[#242629]" />
        </div>
        <button
          onClick={onToggle}
          className="w-[18px] h-[18px] flex items-center justify-center hover:bg-gray-100 rounded"
        >
          <PanelLeftClose className="w-[16px] h-[16px] text-[#242629]" />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-2 space-y-0">
          {navItems.map((item) => (
            <div key={item.label}>
              <button
                className="w-full flex items-center gap-1.5 px-[9px] h-[28px] text-sm text-[#242629] hover:bg-gray-100 rounded-[9px] group font-medium tracking-[-0.28px]"
                onClick={() => item.hasSubmenu && setAutomationsExpanded(!automationsExpanded)}
              >
                <item.icon className="w-3.5 h-3.5 text-[#242629]" />
                <span className="flex-1 text-left">{item.label}</span>
                {item.shortcut && (
                  <span className="text-xs text-gray-400">{item.shortcut}</span>
                )}
                {item.hasSubmenu && (
                  automationsExpanded ?
                    <ChevronDown className="w-3.5 h-3.5 text-[#242629]" /> :
                    <ChevronDown className="w-3.5 h-3.5 text-[#242629]" />
                )}
              </button>
              {item.hasSubmenu && automationsExpanded && (
                <div className="space-y-0">
                  <div className="flex items-start gap-1">
                    <div className="w-4 flex justify-end">
                      <div className="w-px h-[29px] bg-[#eeeff1]"></div>
                    </div>
                    <button className="flex items-center gap-1.5 px-[9px] h-[28px] text-sm text-[#242629] hover:bg-gray-100 rounded-[9px] font-medium tracking-[-0.28px] flex-1">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <div className="w-2 h-2 opacity-60">
                          {/* Send/Sequences icon placeholder */}
                          <div className="w-full h-full bg-[#242629] rounded-sm opacity-50"></div>
                        </div>
                      </div>
                      <span>Sequences</span>
                    </button>
                  </div>
                  <div className="flex items-start gap-1">
                    <div className="w-4 flex justify-end">
                      <div className="w-px h-[29px] bg-[#eeeff1]"></div>
                    </div>
                    <button className="flex items-center gap-1.5 px-[9px] h-[28px] text-sm text-[#242629] hover:bg-gray-100 rounded-[9px] font-medium tracking-[-0.28px] flex-1">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <div className="w-2 h-2 opacity-60">
                          {/* Workflows icon placeholder */}
                          <div className="w-full h-full bg-[#242629] rounded-full opacity-50"></div>
                        </div>
                      </div>
                      <span>Workflows</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>


        {/* Records Section */}
        <div className="px-4 py-2">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            <button
              onClick={() => setRecordsExpanded(!recordsExpanded)}
              className="flex items-center gap-1"
            >
              {recordsExpanded ?
                <ChevronDown className="w-3 h-3" /> :
                <ChevronRight className="w-3 h-3" />
              }
              <span>Records</span>
            </button>
          </div>
          {recordsExpanded && (
            <div className="space-y-1 ml-5">
              {recordItems.map((item) => (
                <button
                  key={item.label}
                  className="w-full flex items-center text-sm"
                >
                  <div className={`flex items-center gap-1.5 px-[9px] h-[28px] w-full rounded-[9px] ${
                    item.active
                      ? 'bg-[#eeeff1]'
                      : 'hover:bg-gray-100'
                  }`}>
                    <div className={`flex items-center justify-center w-4 h-4 ${item.iconBg} rounded-[4.8px] border border-black/5`}>
                      <item.icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-[#242629] text-sm tracking-[-0.28px]">{item.label}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Onboarding Widget - Fixed at bottom */}
      <div className="px-4 py-4 border-t border-gray-200">
        <OnboardingWidget />
      </div>
    </div>
  );
}